<template>
  <NuxtLayout>
    <article :class="{ 'without-page-banner': !pageBanner }">
      <component
        :is="pageBannerComponent"
        v-if="pageBanner && errorType !== 'error'"
        :blok="pageBanner"
        :breadcrumbs="false"
        :extra-padding="hasBlocks"
      />
      <PageIntro
        v-else
        :blok="errorStory"
        :breadcrumbs="false"
        :extra-padding="true"
      />
      <div v-if="hasBlocks" class="page-error-blocks">
        <component
          :is="block.component"
          v-for="block in blocks"
          :key="block._uid"
          :blok="block"
        />
      </div>
    </article>
  </NuxtLayout>
</template>
<script setup lang="ts">
import type { NuxtError } from '#app'
import type { PageIntroStoryblok } from '~/types/storyblok'

const props = defineProps<{
  error: NuxtError
}>()

// Error page story from Storyblok
const errorPageStory =
  await useStoryblokHelpers().fetchAsyncStory('/globals/error')

const errorPageContent: ComputedRef<StoryContentBlok> = computed(
  () => errorPageStory.value?.content as StoryContentBlok,
)
const { pageBanner, pageBannerComponent, blocks } = useStoryContent({
  blok: errorPageContent,
})
const hasBlocks = computed(() => !!blocks.value.length)

// Fallback error page content
const errorType = computed(() => {
  if (props.error.statusCode === 404) {
    return 'page-not-found'
  }
  return 'error'
})
const errorMessage = computed(() => {
  if (Array.isArray(props.error.message)) {
    return props.error.message[0]
  }
  return props.error.message || ''
})
const errorStory: ComputedRef<PageIntroStoryblok> = computed(() => ({
  title:
    errorType.value === 'page-not-found'
      ? 'Page not found'
      : `An error occurred [Error ${props.error.statusCode}]`,
  introduction: errorMessage.value,
  background: 'default',
  blocks: [],
  ctaButton: undefined,
  type: 'basic',
  image: undefined,
  component: 'PageIntro',
  _uid: 'Error',
}))

onMounted(() => {
  console.warn('Error page handled error:: ', props.error)
})
onBeforeUnmount(() => {
  clearError()
})
</script>

<style lang="less" scoped>
.page-error-blocks {
  @media @desktop {
    .relative();

    z-index: 11;
    margin-top: -@gutter-lg * 5;
  }
}
:deep(.columns) {
  @media @tablet {
    .align-center();
  }

  @media @desktop {
    width: @720px;
  }

  > .column {
    flex-basis: auto;
  }
}
</style>
